import { useCallback } from 'react';
import { IconButton } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import IconDelete from 'src/assets/icons/Delete';
import { IconEditPencil } from 'src/assets/icons/IconEditPencil';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { PersonaAvatar } from 'src/components/PersonaAvatar';
import { PersonaPainPoints } from 'src/components/PersonaPainPoints';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { getAgeGroupLabel, getGenderLabel } from 'src/data/demographics';

import { CustomButton } from '../CustomButton';

import { useStyles } from './useStyles';

export const PersonaDetailsPanel = ({
  className,
  persona,
  onEdit,
  onDelete,
  onSave,
  brandVoices,
  isSaving,
}) => {
  const styles = useStyles();

  const { name, avatarType, minAge, maxAge, gender, painPoints, isSuggested } = persona || {};

  const handleDeleteClick = useCallback(() => {
    onDelete?.(persona);
  }, [onDelete, persona]);

  const handleEditClick = useCallback(() => {
    onEdit?.(persona);
  }, [onEdit, persona]);

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.header}>
        <PersonaAvatar
          className={styles.avatar}
          avatarType={avatarType}
        />
        <div>
          <TruncatedTypography
            className={styles.name}
            variant={TYPOGRAPHY_VARIANT.semiBold14}
          >
            {name}
          </TruncatedTypography>
          <CustomTypography
            className={styles.description}
            variant={TYPOGRAPHY_VARIANT.regular12}
          >
            {minAge} - {getAgeGroupLabel(maxAge)} <span>•</span> {getGenderLabel(gender)}
          </CustomTypography>
        </div>

        <div className={styles.actions}>
          <CustomTooltip title="Edit">
            <IconButton
              className={styles.iconButton}
              onClick={handleEditClick}
            >
              <IconEditPencil className={styles.iconEditPersona} />
            </IconButton>
          </CustomTooltip>

          <CustomTooltip title="Delete">
            <IconButton
              className={styles.iconButton}
              onClick={handleDeleteClick}
            >
              <IconDelete />
            </IconButton>
          </CustomTooltip>
        </div>
      </div>
      <PersonaPainPoints
        className={styles.painPoints}
        painPoints={painPoints}
      />
      {isSuggested && (
        <div className={styles.saveButtonContainer}>
          <CustomTooltip title={`Save to your target audiences`}>
            <CustomButton
              variant="contained"
              color="silver"
              loading={isSaving}
              onClick={() => onSave?.(brandVoices[0], persona)}
            >
              Save
            </CustomButton>
          </CustomTooltip>
        </div>
      )}
    </div>
  );
};

PersonaDetailsPanel.propTypes = {
  className: PropTypes.string,
  persona: PropTypes.shape({
    isSuggested: PropTypes.bool,
  }).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSave: PropTypes.func,
  brandVoices: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  isSaving: PropTypes.bool,
};
PersonaDetailsPanel.defaultProps = {
  className: '',
  onEdit: null,
  onDelete: null,
  onSave: null,
  brandVoices: [],
  isSaving: false,
};
